'use client';

import { ButtonHTMLAttributes, ReactNode, MouseEvent } from 'react';
import Loading from '@/components/common/Loading';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  isLoading?: boolean;
  loadingMessage?: string | ReactNode;
  buttonType?:
    | 'primary'
    | 'outlinePrimary'
    | 'secondary'
    | 'outlineSecondary'
    | 'cancel'
    | 'outlineCancel'
    | 'outlineWhite'
    | 'custom'
    | 'outlineCustom';
  customColor?: string;
}

/**
 * 共通ボタン
 * @param param0
 * @returns
 */
export default function Button({
  children,
  className,
  style,
  isLoading,
  loadingMessage = 'saving...',
  disabled,
  buttonType = 'primary',
  customColor,
  onClick,
  ...rest
}: ButtonProps) {
  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (onClick) {
      if (isLoading) return;
      onClick(e);
    }
  };

  // 整合性チェック
  // if (buttonType === "custom" && customColor == null) {
  //   throw new Error('customColor is required')
  // }

  // typeに応じたクラス名を定義
  const primaryButtonClass = 'text-white bg-green-500 hover:bg-green-600';
  const outlinePrimaryButtonClass =
    'text-green-500 border border-green-500 bg-transparet hover:bg-green-500 hover:text-white';
  const secondaryButtonClass = 'text-white bg-gray-500 hover:bg-gray-600';
  const outlineSecondaryButtonClass =
    'text-gray-500 border border-gray-300 bg-transparet hover:bg-gray-100 hover:text-gray-700 focus:outline-none';
  const cancelButtonClass = 'text-white bg-red-500 hover:bg-red-600';
  const outlineCancelButtonClass = 'text-red-500 border border-red-500 bg-transparet hover:bg-red-500 hover:text-white';
  const outlineWhiteButtonClass =
    'text-white border border-white bg-transparet hover:border-gray-300 hover:text-gray-300';
  const customButtonClass = `text-white bg-[${customColor}] hover:opacity-50`;
  const outlineCustomButtonClass = `text-[${customColor}] border border-[${customColor}] bg-transparet hover:opacity-100 hover:text-white'`;

  let buttonClass: string = primaryButtonClass;
  switch (buttonType) {
    case 'primary':
      buttonClass = primaryButtonClass;
      break;
    case 'outlinePrimary':
      buttonClass = outlinePrimaryButtonClass;
      break;
    case 'secondary':
      buttonClass = secondaryButtonClass;
      break;
    case 'outlineSecondary':
      buttonClass = outlineSecondaryButtonClass;
      break;
    case 'cancel':
      buttonClass = cancelButtonClass;
      break;
    case 'outlineCancel':
      buttonClass = outlineCancelButtonClass;
      break;
    case 'outlineWhite':
      buttonClass = outlineWhiteButtonClass;
      break;
    case 'custom':
      buttonClass = customButtonClass;
      break;
    case 'outlineCustom':
      buttonClass = outlineCustomButtonClass;
      break;
    default:
      buttonClass = primaryButtonClass;
      break;
  }

  const disabledClass = 'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-50';
  const defaultClassName = `flex items-center justify-center ${buttonClass} min-w-[120px] font-semibold py-2 px-4 rounded-full ${disabledClass}`;

  const defaultStyle = {
    // 任意のデフォルトスタイルを追加
    // 例: backgroundColor: 'red',
  };

  return (
    <button
      className={`${defaultClassName} ${className || ''}`}
      style={{ ...defaultStyle, ...style }}
      onClick={handleClick}
      disabled={isLoading || disabled}
      {...rest}>
      {isLoading ? <Loading>{loadingMessage}</Loading> : children}
    </button>
  );
}
